export function conversationIdFor(userA: string, userB: string) {
  return [userA, userB].filter(Boolean).sort().join("__")
}

export const OFFICIAL_USER_ID = "SPG2095619699871105024"
export const OFFICIAL_USERNAME = "KLIPO"
export const STARRED_USERNAMES = ["klipo", "reelhood", "reelmfer"]
export const SEED_CREATOR_ID = "SPC00000000000000000000"

export function isSeedUserId(userId?: string) {
  const id = (userId || "").trim()
  if (!id) return false
  if (id === SEED_CREATOR_ID) return true
  return id.startsWith("demo-") || id.startsWith("creator-")
}

export function isSeedProfile(profile?: { userId?: string; creator?: string } | null) {
  if (!profile) return false
  return isSeedUserId(profile.userId) || profile.creator === SEED_CREATOR_ID
}

export function sanitizeHandle(value: string) {
  return value.trim().replace(/^@+/, "").replace(/\s+/g, "")
}

export function normalizeUsername(value: string) {
  return sanitizeHandle(value).toLowerCase()
}

export function isOfficialAccount(profile?: { userId?: string; username?: string } | null) {
  return Boolean(profile?.userId && profile.userId === OFFICIAL_USER_ID)
}

export function isKlipoAdmin(user?: { userId?: string; userRole?: string; email?: string; userName?: string } | null) {
  if (!user) return false
  if (user.userRole === "ADMIN") return true
  if (user.userId && user.userId === OFFICIAL_USER_ID) return true
  const email = String(user.email || "").trim().toLowerCase()
  const local = email.split("@")[0] || ""
  if (local === "klipo") return true
  const name = normalizeUsername(String(user.userName || ""))
  return name === normalizeUsername(OFFICIAL_USERNAME)
}

export function hasGreenStar(profile?: { userId?: string; username?: string; displayName?: string } | null) {
  if (isOfficialAccount(profile)) return true
  const names = [profile?.username, profile?.displayName].map((value) => normalizeUsername(value || ""))
  return names.some((name) => STARRED_USERNAMES.includes(name))
}

export function isReservedOfficialUsername(value: string) {
  return normalizeUsername(value) === normalizeUsername(OFFICIAL_USERNAME)
}

export function sanitizeUrl(value: string) {
  const trimmed = value.trim()
  if (!trimmed) return ""
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  return `https://${trimmed}`
}

export function instagramUrl(handle: string) {
  const clean = sanitizeHandle(handle)
  return clean ? `https://instagram.com/${clean}` : ""
}

export function xUrl(handle: string) {
  const clean = sanitizeHandle(handle)
  return clean ? `https://x.com/${clean}` : ""
}

export function tiktokUrl(handle: string) {
  const clean = sanitizeHandle(handle)
  return clean ? `https://tiktok.com/@${clean}` : ""
}

export function isOwnProfile(viewingId?: string, authUserId?: string) {
  return Boolean(viewingId && authUserId && viewingId === authUserId)
}

export function toPublicProfile<T extends { userId?: string; walletAddress?: string; creatorEmail?: string; creator?: string }>(profile: T | null): T | null {
  if (!profile) return null
  const publicProfile = {
    ...profile,
    walletAddress: "",
    creatorEmail: undefined,
    creator: undefined,
    interests: undefined,
    privacy: undefined,
    onboarded: undefined,
  }
  return publicProfile
}

export function publicProfilesFor<T extends { userId?: string; walletAddress?: string; creatorEmail?: string; creator?: string }>(
  profiles: T[] | undefined,
  allowedUserIds?: string[],
) {
  const allowed = allowedUserIds ? new Set(allowedUserIds.filter(Boolean)) : null
  return (profiles || [])
    .filter((profile) => Boolean(profile?.userId) && (!allowed || allowed.has(profile.userId || "")))
    .map((profile) => toPublicProfile(profile))
    .filter((profile): profile is T => Boolean(profile))
}

export function profileHandle(profile?: { username?: string; handle?: string } | null) {
  return sanitizeHandle(profile?.username || profile?.handle || "")
}

export function usernameTaken(profiles: Array<{ userId?: string; username?: string; handle?: string }>, username: string, userId: string) {
  const wanted = normalizeUsername(username)
  if (!wanted) return false
  return profiles.some((item) => {
    if (!item?.userId || item.userId === userId) return false
    return normalizeUsername(profileHandle(item)) === wanted
  })
}

export function emptyProfileForm() {
  return {
    username: "",
    displayName: "",
    bio: "",
    walletAddress: "",
    avatarUrl: "",
    websiteUrl: "",
    instagramHandle: "",
    xHandle: "",
    tiktokHandle: "",
    youtubeUrl: "",
    city: "",
    interests: "",
    privacy: "public" as const,
  }
}
